import firebase from "firebase/app";
import { auth, db } from "./config";

/**
 * Google sign-in for site visitors — the only thing this unlocks is viewing a
 * handyman/agency's contact details and leaving a review. Mirrors the app's
 * Database/googleAuth.js, minus the phone-completeness gate: a website visitor
 * doesn't need a saved phone number for anything the site does.
 */

const suggestName = (user) => {
  if (user.displayName && user.displayName.trim()) return user.displayName.trim();
  return user.email ? user.email.split("@")[0] : "Rekebisha member";
};

// Creates users/{uid} for a brand new member; for a returning member it only fills in
// blanks, so nothing already saved (e.g. by the app) is ever overwritten.
const ensureUserDocument = async (user) => {
  const userDocRef = db.collection("users").doc(user.uid);
  const snapshot = await userDocRef.get();

  if (!snapshot.exists) {
    await userDocRef.set({
      email: user.email || "",
      username: suggestName(user),
      photoURL: user.photoURL || "",
      authProvider: "google.com",
      createdAt: new Date().toISOString(),
    });
    return;
  }

  const data = snapshot.data();
  const patch = {};
  if (!data.email && user.email) patch.email = user.email;
  if (!data.photoURL && user.photoURL) patch.photoURL = user.photoURL;
  if (!data.username) patch.username = suggestName(user);

  if (Object.keys(patch).length > 0) {
    await userDocRef.set(patch, { merge: true });
  }
};

/** Opens the Google popup, then makes sure users/{uid} exists. */
export const signInWithGoogle = async () => {
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  const result = await auth.signInWithPopup(provider);
  await ensureUserDocument(result.user);
  return result.user;
};

export const signOutMember = () => auth.signOut();

/** Turns a Google / Firebase failure into something worth showing a visitor. */
export const describeGoogleSignInError = (error) => {
  switch (error?.code) {
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return null; // they backed out, nothing to report
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in popup. Allow popups for this site and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/unauthorized-domain":
      return "Google sign-in isn't enabled for this domain yet.";
    default:
      return error?.message || "Could not sign in with Google. Please try again.";
  }
};
